import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { activeCompanyId, currentUserId } from '../scripts/chat.auth';
import { useFloatingDrawer } from './FloatingDrawerContext';
import {
  ChatConversation, ChatMessage,
  getConversations, getConversation,
  getMessages as fetchMessages,
  postMessage, markRead as apiMarkRead,
  findOrCreateConversation, getCompanyMembers,
  CompanyMember,
} from '../services/chat/chat.service';
import { useChatWebSocket, ChatWsStatus, WsMessageCreated, WsMessageRead } from '../hooks/useChatWebSocket';

// --- Context shape ---
interface ChatContextState {
  conversations: ChatConversation[];
  activeConversationId: string | null;
  messages: ChatMessage[];
  loadingMessages: boolean;
  unreadTotal: number;
  wsStatus: ChatWsStatus;
  members: CompanyMember[] | null;
  membersUnavailable: boolean;
  openConversation: (convOrId: ChatConversation | string) => void;
  openConversationWithUser: (targetUserId: string) => void;
  sendMessage: (content: string) => void;
  markConversationRead: (convId: string) => void;
  refreshConversations: () => void;
  closeThread: () => void;
  loadMembers: (targetCompanyId?: string) => void;
  getConversationName: (conv: ChatConversation) => string;
  getConversationSubtitle: (conv: ChatConversation) => string | null;
  getConversationAvatar: (conv: ChatConversation) => string | null;
}

const ChatContext = createContext<ChatContextState | null>(null);

export function useChat(): ChatContextState {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    // Return a safe no-op state when provider is not mounted (e.g. no active company)
    return {
      conversations: [],
      activeConversationId: null,
      messages: [],
      loadingMessages: false,
      unreadTotal: 0,
      wsStatus: 'closed',
      members: null,
      membersUnavailable: false,
      openConversation: () => {},
      openConversationWithUser: () => {},
      sendMessage: () => {},
      markConversationRead: () => {},
      refreshConversations: () => {},
      closeThread: () => {},
      loadMembers: () => {},
      getConversationName: () => '',
      getConversationSubtitle: () => null,
      getConversationAvatar: () => null,
    };
  }
  return ctx;
}

export function useChatOptional(): ChatContextState | null {
  return useContext(ChatContext);
}

// --- Provider ---
export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const companyId = activeCompanyId();
  const { isOpen, activeMode } = useFloatingDrawer();

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [members, setMembers] = useState<CompanyMember[] | null>(null);
  const [membersUnavailable, setMembersUnavailable] = useState(false);

  const activeConvIdRef = useRef<string | null>(null);
  activeConvIdRef.current = activeConversationId;

  const isChatVisible = isOpen && activeMode === 'chat';
  const isChatVisibleRef = useRef(isChatVisible);
  isChatVisibleRef.current = isChatVisible;

  // --- Compute unreadTotal ---
  const unreadTotal = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  // --- Seed conversations on mount ---
  const refreshConversations = useCallback(() => {
    if (!companyId) return;
    getConversations()
      .then(setConversations)
      .catch(() => setConversations([]));
  }, [companyId]);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  // --- Helpers ---
  const getConversationName = useCallback((conv: ChatConversation): string => {
    const myId = currentUserId();
    const active = conv.participants.filter(p => !p.left_at);
    const other = active.find(p => p.user_id !== myId);
    if (other) return other.display_name || '?';
    return active.map(p => p.display_name || '?').join(', ');
  }, []);

  const getConversationSubtitle = useCallback((conv: ChatConversation): string | null => {
    const myId = currentUserId();
    const other = conv.participants.find(p => p.user_id !== myId);
    return other?.acting_as_company_name ?? null;
  }, []);

  const getConversationAvatar = useCallback((_conv: ChatConversation): string | null => {
    // The chat API does not return avatar URLs — always use initials
    return null;
  }, []);

  // --- Open a conversation (from list or by id for deep links) ---
  const openConversation = useCallback(async (convOrId: ChatConversation | string) => {
    const convId = typeof convOrId === 'string' ? convOrId : convOrId.id;
    if (!convId) return;
    setActiveConversationId(convId);
    setLoadingMessages(true);
    try {
      // If not in list (deep link), fetch and add
      const inList = typeof convOrId !== 'string';
      if (!inList) {
        const fetched = await getConversation(convId);
        setConversations(prev => {
          const exists = prev.find(c => c.id === convId);
          if (exists) return prev;
          return [fetched, ...prev];
        });
      }
      const data = await fetchMessages(convId);
      const sorted = (data.items ?? []).slice().sort((a, b) => a.created_at.localeCompare(b.created_at));
      // Diagnostic: log identity matching for the report — remove after validation
      const conv = typeof convOrId !== 'string' ? convOrId : null;
      const myId = currentUserId();
      console.log('[chat debug] currentUserId()=', myId,
        '| participants=', conv?.participants?.map(p => p.user_id),
        '| first msg sender_id=', sorted[0]?.sender_id);
      setMessages(sorted);
      apiMarkRead(convId).catch(() => {});
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c));
    } catch {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // --- Open or create a conversation with a specific user (from contacts) ---
  const openConversationWithUser = useCallback(async (targetUserId: string) => {
    if (!companyId) return;
    setLoadingMessages(true);
    try {
      const conv = await findOrCreateConversation(targetUserId, companyId);
      if (!conv.id) throw new Error('Empty conversation id');
      setActiveConversationId(conv.id);
      const data = await fetchMessages(conv.id);
      const sorted = (data.items ?? []).slice().sort((a, b) => a.created_at.localeCompare(b.created_at));
      setMessages(sorted);
      apiMarkRead(conv.id).catch(() => {});
      // Ensure conv is in list
      setConversations(prev => {
        const exists = prev.find(c => c.id === conv.id);
        if (exists) return prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c);
        return [{ ...conv, unread_count: 0 }, ...prev];
      });
    } catch {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, [companyId]);

  // --- Send message (optimistic) ---
  const sendMessage = useCallback((content: string) => {
    const text = content.trim();
    const convId = activeConvIdRef.current;
    if (!text || !convId) return;

    const myId = currentUserId();
    const optimistic: ChatMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: convId,
      sender_id: myId,
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);

    // Update last_message in conversations list
    setConversations(prev => {
      const updated = prev.map(c => c.id === convId ? {
        ...c,
        last_message: { id: optimistic.id, conversation_id: convId, sender_id: myId, content: text, created_at: optimistic.created_at },
        updated_at: optimistic.created_at,
      } : c);
      // Move to top
      const target = updated.find(c => c.id === convId);
      if (target) return [target, ...updated.filter(c => c.id !== convId)];
      return updated;
    });

    postMessage(convId, text)
      .then(saved => {
        setMessages(prev => prev.map(m => m.id === optimistic.id ? saved : m));
      })
      .catch(() => {
        setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      });
  }, []);

  // --- Mark read ---
  const markConversationRead = useCallback((convId: string) => {
    apiMarkRead(convId).catch(() => {});
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c));
  }, []);

  // --- Close thread (back to list) ---
  const closeThread = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setMembers(null);
    setMembersUnavailable(false);
  }, []);

  // --- Load company members ---
  const loadMembers = useCallback((targetCompanyId?: string) => {
    const id = targetCompanyId || companyId;
    if (!id) return;
    setMembers(null);
    setMembersUnavailable(false);
    getCompanyMembers(id)
      .then(setMembers)
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
          setMembersUnavailable(true);
        }
        setMembers([]);
      });
  }, [companyId]);

  // --- WS event handlers ---
  const onMessageCreated = useCallback((data: WsMessageCreated) => {
    const convId = data.conversation_id;
    const msg: ChatMessage = {
      id: data._id ?? (data as any).id ?? '',
      conversation_id: convId,
      sender_id: data.sender_user_id ?? (data as any).sender_id ?? '',
      content: data.content ?? '',
      created_at: data.created_at ?? '',
      read_by: data.read_by,
    };

    const isThreadOpen = activeConvIdRef.current === convId;
    const isVisible = isChatVisibleRef.current;

    if (isThreadOpen && isVisible) {
      // Thread is open and visible: append message + auto-mark-read (don't increment unread)
      setMessages(prev => [...prev, msg]);
      apiMarkRead(convId).catch(() => {});
      // Update last_message + move to top, keep unread at 0
      setConversations(prev => {
        const updated = prev.map(c => c.id === convId ? {
          ...c,
          last_message: { id: msg.id, conversation_id: convId, sender_id: msg.sender_id, content: msg.content, created_at: msg.created_at },
          updated_at: msg.created_at,
          unread_count: 0,
        } : c);
        const target = updated.find(c => c.id === convId);
        if (target) return [target, ...updated.filter(c => c.id !== convId)];
        return updated;
      });
    } else {
      // Thread not open or not visible: increment unread
      setConversations(prev => {
        const existing = prev.find(c => c.id === convId);
        if (!existing) {
          // Unknown conversation (created from mobile) — fetch and insert
          getConversation(convId)
            .then(fetched => {
              setConversations(p => {
                if (p.find(c => c.id === convId)) return p;
                return [{ ...fetched, unread_count: 1 }, ...p];
              });
            })
            .catch(() => {});
          return prev;
        }
        const updated = prev.map(c => c.id === convId ? {
          ...c,
          last_message: { id: msg.id, conversation_id: convId, sender_id: msg.sender_id, content: msg.content, created_at: msg.created_at },
          updated_at: msg.created_at,
          unread_count: (c.unread_count || 0) + 1,
        } : c);
        const target = updated.find(c => c.id === convId);
        if (target) return [target, ...updated.filter(c => c.id !== convId)];
        return updated;
      });
    }
  }, []);

  const onMessageRead = useCallback((data: WsMessageRead) => {
    const convId = data.conversation_id;
    // Update read_by in current thread messages if open
    if (activeConvIdRef.current === convId) {
      setMessages(prev => prev.map(m => {
        if (m.conversation_id !== convId) return m;
        const readBy = m.read_by ?? [];
        if (readBy.some(r => typeof r === 'string' ? r === data.user_id : (r as any).user_id === data.user_id)) return m;
        return { ...m, read_by: [...readBy, data.user_id] };
      }));
    }
  }, []);

  // --- WS connection (permanent) ---
  const { status: wsStatus } = useChatWebSocket({
    onMessageCreated,
    onMessageRead,
  });

  const value: ChatContextState = {
    conversations,
    activeConversationId,
    messages,
    loadingMessages,
    unreadTotal,
    wsStatus,
    members,
    membersUnavailable,
    openConversation,
    openConversationWithUser,
    sendMessage,
    markConversationRead,
    refreshConversations,
    closeThread,
    loadMembers,
    getConversationName,
    getConversationSubtitle,
    getConversationAvatar,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// --- Always mount ChatProvider so useChat() never throws ---
export const ChatProviderGated: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ChatProvider>{children}</ChatProvider>;
};
